import {
  isAdmin as isAdminRule,
  isExecutor as isExecutorRule,
  isGerente as isGerenteRule,
  isGestor as isGestorRule,
  isGhostAdmin as isGhostAdminRule,
  isPendente as isPendenteRule,
  resolveSettings,
  SETTING_DEFAULTS,
  SYSTEM_ACTOR_NAME,
  systemName,
  todayInSaoPaulo,
  type GlobalRole,
  type Me,
  type Settings,
} from '@supply/contracts'
import { LogIn } from 'lucide-react'
import * as React from 'react'
import { toast } from 'sonner'

import { ErrorState, Forbidden, FullScreen, Loading, Pending } from '@/components/states'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { endViewAs, loadReference, myAccessStatus, type Reference } from '@/data/api'
import { armMock, clearMockRequest, disarmMock, hasMockRequest, shouldArmMock } from '@/data/mock/mode'
import type { Area, AreaMember, Member } from '@/data/types'
import { invalidate, refreshAll, replaceEqualDeep } from '@/data/use-load'
import { fetchBridgeSession, getIsSigningOut, redirectToBridgeLogin, signOutFromBridge } from '@/lib/bridge-auth'
import { currentDevUserId, DEV_PROFILES, devSignIn, devSignOut } from '@/lib/dev-auth'
import { appEnv } from '@/lib/env'
import { clearViewAs, getViewAs } from '@/lib/view-as'

/**
 * Sessão + dados de referência (áreas, pessoas, vínculos, metas).
 *
 * O acesso tem DUAS etapas, e as duas precisam passar:
 *   1. SSO — `GET /api/session` no spa-bridge diz quem é a pessoa e entrega o
 *      JWT que autoriza o PostgREST;
 *   2. Supply — a pessoa precisa de ficha ATIVA em `supply.member`. Entrar no
 *      SSO não é o mesmo que ser do Supply.
 *
 * INVIOLÁVEL: papéis e áreas daqui servem para ESCONDER o que a pessoa não pode
 * usar. A barreira real é o RLS — quem contorna a tela esbarra no banco.
 */

export interface SessionState {
  readonly member: Member
  readonly me: Me
  readonly isGestor: boolean
  /** Responsável de alguma área: vê o time dele (ADR 0006). */
  readonly isGerente: boolean
  /** Executor: só vínculos `executor`; visão operacional, recortada no banco. */
  readonly isExecutor: boolean
  /**
   * Referência OPERACIONAL: sem os dev admins fantasmas (ADR 0008). Toda lista,
   * seletor, contagem e relatório sai daqui, então o admin não aparece em nada.
   */
  readonly ref: Reference
  /** Dev admin (papel `admin`), inclusive no "ver como" de outro admin. */
  readonly isAdmin: boolean
  /** Ids dos admins fantasmas (o banco entrega só os ids a quem não é admin). */
  readonly ghostIds: ReadonlySet<string>
  /** Fichas dos admins fantasmas: só para o admin (seção Administradores). */
  readonly admins: readonly Member[]
  readonly areaById: ReadonlyMap<string, Area>
  /** Nome/avatar por id. O admin fantasma vira "Administração do sistema" para quem não é admin. */
  readonly memberById: ReadonlyMap<string, Member>
  /** Responsável de cada área (id da área → membro). */
  readonly responsavelOf: ReadonlyMap<string, Member>
  readonly activeAreas: readonly Area[]
  /** Configurações do gestor já com os padrões (`supply.setting`). */
  readonly settings: Settings
  /** `remote`: veio do tempo real (outra pessoa mudou), não de escrita minha. */
  readonly reloadReference: (options?: { remote?: boolean }) => Promise<void>
  readonly signOut: () => Promise<void>
}

const SessionContext = React.createContext<SessionState | null>(null)

type Status =
  | { kind: 'carregando' }
  | { kind: 'anonimo' }
  | { kind: 'login-local' }
  | { kind: 'sem-acesso'; detail?: string }
  | { kind: 'pendente'; member: Pick<Member, 'id' | 'full_name' | 'email'> }
  | { kind: 'erro'; detail: string }
  | { kind: 'ok'; member: Member; ref: Reference }

async function resolve(userId: string): Promise<Status> {
  // Quem não tem ficha lê zero linhas (RLS exige membro ativo) — é assim que se
  // detecta "entrou no SSO mas não é do Supply", sem função extra.
  const ref = await loadReference()
  // "Ver como" (ADR 0007): a sessão é a da pessoa-alvo. Quem garante que pode
  // é o banco — sem sessão válida do gestor o loadReference acima já falhou.
  const effectiveUserId = getViewAs()?.targetUserId ?? userId
  const member = ref.members.find((m) => m.user_id === effectiveUserId && m.is_active)
  if (!member) {
    // Ficha sem papel nem área não lê nem a própria linha (`is_member`): o banco
    // diz se é "pendente" ou "sem ficha".
    const access = await myAccessStatus().catch(() => null)
    if (access?.status === 'pendente' && access.member_id) {
      return {
        kind: 'pendente',
        member: { id: access.member_id, full_name: access.full_name ?? '', email: access.email ?? '' },
      }
    }
    return { kind: 'sem-acesso' }
  }
  // Ficha sem papel nem cargo (importada do SSO sem cargo): ainda não usa o app.
  const me = buildMe(member, ref)
  if (isPendenteRule(me)) return { kind: 'pendente', member: member }
  // Dados fictícios (SIS-465): o papel acabou de vir do banco REAL. Só o dev
  // admin arma; qualquer outro pedido no sessionStorage é descartado.
  if (shouldArmMock(me, getViewAs() !== null)) {
    try {
      if (await armMock(member, me, todayInSaoPaulo())) return { kind: 'ok', member, ref: await loadReference() }
    } catch (error) {
      console.error('Falha ao montar os dados fictícios; seguindo com os reais', error)
      disarmMock()
      clearMockRequest()
    }
  } else if (hasMockRequest()) {
    clearMockRequest()
  }
  return { kind: 'ok', member, ref }
}

/** O admin fantasma como autor, para quem não é admin: sem nome, e-mail nem avatar. */
function systemActor(id: string): Member {
  return { id, user_id: null, email: '', full_name: SYSTEM_ACTOR_NAME, avatar_url: null, is_active: true }
}

function buildMe(member: Member, ref: Reference): Me {
  return {
    memberId: member.id,
    roles: ref.roles.filter((r) => r.member_id === member.id).map((r) => r.role as GlobalRole),
    areas: ref.areaMembers
      .filter((am: AreaMember) => am.member_id === member.id)
      .map((am) => ({ areaId: am.area_id, role: am.role })),
  }
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = React.useState<Status>({ kind: 'carregando' })
  const [attempt, setAttempt] = React.useState(0)

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        if (appEnv.authMode === 'local') {
          const userId = await currentDevUserId()
          if (!userId) {
            if (!cancelled) setStatus({ kind: 'login-local' })
            return
          }
          const next = await resolve(userId)
          if (!cancelled) setStatus(next)
          return
        }

        const session = await fetchBridgeSession()
        if (!session) {
          if (!cancelled) setStatus({ kind: 'anonimo' })
          return
        }
        if (!session.supabaseAccessToken) {
          if (!cancelled) {
            setStatus({
              kind: 'sem-acesso',
              detail:
                'O SSO autenticou, mas não emitiu o token de acesso aos dados. Verifique o cadastro do app `supply` no portal.',
            })
          }
          return
        }
        const next = await resolve(session.userId)
        if (!cancelled) setStatus(next)
      } catch (error) {
        // Sessão de visualização expirada/encerrada: a aba volta à visão do gestor.
        if (getViewAs()) {
          clearViewAs()
          window.location.assign('/')
          return
        }
        if (!cancelled) setStatus({ kind: 'erro', detail: error instanceof Error ? error.message : 'Falha desconhecida' })
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [attempt])

  // Anônimo entra no handoff do SSO. A guarda de logout evita "cliquei em sair
  // e voltei logado".
  React.useEffect(() => {
    if (appEnv.authMode === 'local') return
    if (status.kind === 'anonimo' && !getIsSigningOut()) redirectToBridgeLogin(window.location.href)
  }, [status.kind])

  const signOut = React.useCallback(async () => {
    // Sair desliga os dados fictícios desta aba.
    clearMockRequest()
    disarmMock()
    if (appEnv.authMode === 'local') {
      await devSignOut()
      window.location.assign('/')
      return
    }
    const logoutUrl = await signOutFromBridge()
    window.location.replace(logoutUrl ?? '/login?auth_error=logged_out')
  }, [])

  const reloadReference = React.useCallback(async (options?: { remote?: boolean }) => {
    const ref = await loadReference()
    // Sem mudança, a sessão segue a mesma (nada re-renderiza à toa).
    setStatus((s) => {
      if (s.kind !== 'ok') return s
      const next = replaceEqualDeep(s.ref, ref)
      return next === s.ref ? s : { ...s, ref: next }
    })
    if (options?.remote) refreshAll()
    else invalidate()
  }, [])

  // Configurações não carregaram: aviso discreto, uma vez por motivo; a sessão segue com os padrões.
  const settingsError = status.kind === 'ok' ? status.ref.settingsError : null
  React.useEffect(() => {
    if (settingsError) {
      toast.warning('Configurações indisponíveis', { id: 'settings-error', description: 'Usando os valores padrão por enquanto.' })
    }
  }, [settingsError])

  const value = React.useMemo<SessionState | null>(() => {
    if (status.kind !== 'ok') return null
    const { member, ref: fullRef } = status
    const me = buildMe(member, fullRef)
    // A RPC `ghost_member_ids` nunca trava o login (falhou = lista vazia). Para quem
    // é admin o RLS entrega todas as fichas, então o fantasma também sai da regra
    // (`isGhostAdmin`) — o admin nunca reaparece nas listas por uma falha de rede.
    const ghostIds = new Set([...fullRef.ghostIds, ...fullRef.members.filter((m) => isGhostAdminRule(buildMe(m, fullRef))).map((m) => m.id)])
    const isAdmin = isAdminRule(me)
    const ref: Reference = ghostIds.size
      ? {
          ...fullRef,
          members: fullRef.members.filter((m) => !ghostIds.has(m.id)),
          roles: fullRef.roles.filter((r) => !ghostIds.has(r.member_id)),
          areaMembers: fullRef.areaMembers.filter((am) => !ghostIds.has(am.member_id)),
        }
      : fullRef
    const admins = isAdmin ? fullRef.members.filter((m) => ghostIds.has(m.id)) : []
    const areaById = new Map(ref.areas.map((a) => [a.id, a]))
    const memberById = new Map(ref.members.map((m) => [m.id, m]))
    // Autoria: o admin vê o autor real (auditoria); os demais, "Administração do sistema".
    for (const id of ghostIds) {
      const real = fullRef.members.find((m) => m.id === id)
      memberById.set(id, isAdmin && real ? real : systemActor(id))
    }
    const responsavelOf = new Map<string, Member>()
    for (const am of ref.areaMembers) {
      const m = memberById.get(am.member_id)
      if (am.role === 'responsavel' && m?.is_active) responsavelOf.set(am.area_id, m)
    }
    return {
      member,
      me,
      isGestor: isGestorRule(me),
      isGerente: isGerenteRule(me),
      isExecutor: isExecutorRule(me),
      ref,
      isAdmin,
      ghostIds,
      admins,
      areaById,
      memberById,
      responsavelOf,
      activeAreas: ref.areas.filter((a) => a.is_active),
      settings: resolveSettings(ref.settings),
      reloadReference,
      signOut,
    }
  }, [status, reloadReference, signOut])

  // Nome do sistema (Configurações → Sistema) no título da aba; guardado para as
  // telas de antes da sessão (login, sem acesso), que não leem o banco.
  const brand = value ? systemName(value.settings) : cachedSystemName()
  React.useEffect(() => {
    document.title = brand
    try {
      window.localStorage.setItem(BRAND_KEY, brand)
    } catch {
      /* storage indisponível */
    }
  }, [brand])

  if (status.kind === 'carregando' || status.kind === 'anonimo') {
    return (
      <FullScreen>
        <Loading label={status.kind === 'anonimo' ? 'Redirecionando para o login...' : 'Verificando sessão...'} />
      </FullScreen>
    )
  }
  if (status.kind === 'login-local') {
    return <LocalLogin onDone={() => setAttempt((n) => n + 1)} />
  }
  if (status.kind === 'erro') {
    return (
      <FullScreen>
        <ErrorState
          title="Não foi possível carregar a sessão"
          description={status.detail}
          onRetry={() => {
            setStatus({ kind: 'carregando' })
            setAttempt((n) => n + 1)
          }}
        />
      </FullScreen>
    )
  }
  if (status.kind === 'sem-acesso') {
    return (
      <FullScreen>
        <Forbidden
          description={
            status.detail ??
            `Sua conta entrou no SSO, mas ainda não tem acesso à ${brand}. Peça a liberação ao gestor.`
          }
        />
        <div className="mt-4 flex justify-center">
          <Button variant="outline" size="sm" onClick={() => void signOut()}>
            Sair
          </Button>
        </div>
      </FullScreen>
    )
  }

  if (status.kind === 'pendente') {
    const viewAs = getViewAs()
    return (
      <FullScreen>
        <Pending
          title="Acesso pendente"
          description={
            viewAs
              ? `${status.member.full_name} entrou pelo SSO, mas ainda não tem papel nem cargo na ${brand}: é isto que ela vê.`
              : `Sua conta (${status.member.email}) entrou pelo SSO, mas ainda não tem papel nem cargo na ${brand}. Fale com o administrador para liberar o acesso.`
          }
        />
        <div className="mt-4 flex justify-center">
          {viewAs ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void endViewAs(viewAs.sessionId, 'saiu')
                  .catch(() => undefined)
                  .finally(() => {
                    clearViewAs()
                    window.location.assign('/')
                  })
              }}
            >
              Sair do modo visualização
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => void signOut()}>
              Sair
            </Button>
          )}
        </div>
      </FullScreen>
    )
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

/** Só em `pnpm dev` com VITE_AUTH_MODE=local: escolhe um perfil do seed. */
function LocalLogin({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = React.useState<string>(DEV_PROFILES[0].email)
  const [error, setError] = React.useState<string | null>(null)
  const [pending, setPending] = React.useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setPending(true)
    setError(null)
    try {
      await devSignIn(email)
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha no login local')
    } finally {
      setPending(false)
    }
  }

  return (
    <FullScreen>
      <form onSubmit={(e) => void submit(e)} className="space-y-4 rounded-md border border-border bg-card p-6 shadow-sm">
        <div className="space-y-1">
          <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-primary">Ambiente local</div>
          <h1 className="text-xl font-semibold tracking-tight">{cachedSystemName()}</h1>
          <p className="text-sm text-muted-foreground">
            Sem SSO: escolha um perfil de teste do seed para conferir o que cada pessoa vê e pode fazer.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="dev-profile">Perfil</Label>
          <Select value={email} onValueChange={setEmail}>
            <SelectTrigger id="dev-profile">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DEV_PROFILES.map((p) => (
                <SelectItem key={p.email} value={p.email}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <Button type="submit" className="w-full" disabled={pending}>
          <LogIn /> {pending ? 'Entrando...' : 'Entrar'}
        </Button>
      </form>
    </FullScreen>
  )
}

export function useSession(): SessionState {
  const context = React.useContext(SessionContext)
  if (!context) throw new Error('useSession precisa estar dentro de <SessionProvider>')
  return context
}

/** Sessão, se houver — para quem também roda fora dela (tela de erro, chamado). */
export function useOptionalSession(): SessionState | null {
  return React.useContext(SessionContext)
}

const BRAND_KEY = 'supply:sistema-nome'

/** Último nome do sistema visto neste navegador (ou o padrão). */
function cachedSystemName(): string {
  try {
    return window.localStorage.getItem(BRAND_KEY) || systemName(SETTING_DEFAULTS)
  } catch {
    return systemName(SETTING_DEFAULTS)
  }
}

/** Configurações do gestor; fora da sessão, os padrões. */
export function useSettings(): Settings {
  return React.useContext(SessionContext)?.settings ?? SETTING_DEFAULTS
}

/** Nome curto de uma pessoa (ou o texto livre do executor). */
export function useMemberName() {
  const { memberById } = useSession()
  return React.useCallback(
    (id: string | null | undefined, fallback = '—') => (id ? (memberById.get(id)?.full_name ?? fallback) : fallback),
    [memberById],
  )
}
